import React, { createContext, useContext, useState, useCallback } from 'react';

interface BreadcrumbContextType {
    labels: Record<string, string>;
    setLabel: (pathSegment: string, label: string) => void;
    removeLabel: (pathSegment: string) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

export const BreadcrumbProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [labels, setLabels] = useState<Record<string, string>>({
        'random-wisdom': 'Random Wisdom Entry',  // Guest Features Phase 2
        'forgot-password': 'Forgot Password',  // Phase 12: Password reset
        'reset-password': 'Reset Password'  // Phase 12: Password reset
    });

    const setLabel = useCallback((pathSegment: string, label: string) => {
        setLabels((prev) => ({ ...prev, [pathSegment]: label }));
    }, []);

    const removeLabel = useCallback((pathSegment: string) => {
        setLabels((prev) => {
            const { [pathSegment]: _, ...rest } = prev;
            return rest;
        });
    }, []);

    return (
        <BreadcrumbContext.Provider value={{ labels, setLabel, removeLabel }}>
            {children}
        </BreadcrumbContext.Provider>
    );
};

export const useBreadcrumb = (pathSegment?: string, label?: string) => {
    const context = useContext(BreadcrumbContext);
    if (!context) {
        throw new Error('useBreadcrumb must be used within a BreadcrumbProvider');
    }

    React.useEffect(() => {
        if (pathSegment && label) {
            context.setLabel(pathSegment, label);
            return () => context.removeLabel(pathSegment);
        }
    }, [pathSegment, label, context]);

    return context;
};
